import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlService } from '../url/url.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import { mapToXPagination } from '../../models/common/pagination.model';
import { ChatbotDto, ChatbotParamsDto, PaginatedChatbotsDto } from '../../models/chatbot/chatbot.dto';
import { CreateChatbotDto } from '../../models/chatbot/create-chatbot.dto';
import { UpdateChatbotDto } from '../../models/chatbot/update-chatbot.dto';

@Injectable({
  providedIn: 'root'
})
export class ChatbotApiService {
  constructor(
    private http: HttpClient,
    private urlService: UrlService) { }

  /**
   * Retrieves all the chatbots.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.chatbotApiService.getChatbotApi(params);
   *
   * // Use this in Interceptors, Guards and Functions
   * chatbotApiService.getChatbotApi(params);
   * ```
   *
   * @param params - Flow  params dto
   * @returns Observable<PaginatedChatbotDto> - All chatbots
   */
  public getChatbotApi(params: ChatbotParamsDto): Observable<PaginatedChatbotsDto> {
    const url = this.urlService.getChatbotUrl();

    return this.http.get<ChatbotDto[]>(url, {
      params: {
        ...(params.PageNumber !== undefined && { PageNumber: params.PageNumber.toString() }),
        ...(params.PageSize !== undefined && { PageSize: params.PageSize.toString() }),
        ...(params.AllowToTechnician !== undefined && { AllowToTechnician: params.AllowToTechnician.toString() }),
        ...(typeof params.Status === 'number' && { Status: params.Status }),
      },
      observe: 'response',
    }).pipe(
      map((response: HttpResponse<ChatbotDto[]>) => {
        const chatbots = response.body ?? [];
        const paginationHeader = response.headers.get('x-pagination');
        const pagination = mapToXPagination(paginationHeader);

        return { chatbots, pagination };
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  getCurrentModeChatbotStatusApi(): Observable<ChatbotDto> {
    const url = this.urlService.getChatbotUrl() + '/Mode';
    return this.http.get<ChatbotDto>(url).pipe(
          catchError(error => {
            return throwError(() => error);
          })
        );
  }

  /**
   * Retrieves a chatbot by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.chatbotApiService.getChatbotByIdApi(chatbotId);
   *
   * // Use this in Interceptors, Guards and Functions
   * chatbotApiService.getChatbotByIdApi(chatbotId);
   * ```
   *
   * @param chatbotId - The unique identifier of the chatbot
   * @returns Observable<ChatbotDto> - The chatbot details
   */
  getChatbotByIdApi(chatbotId: string): Observable<ChatbotDto> {
    const url = this.urlService.getChatbotByIdUrl(chatbotId);
    return this.http.get<ChatbotDto>(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Creates a new chatbot
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.chatbotApiService.createChatbotApi(chatbot);
   *
   * // Use this in Interceptors, Guards and Functions
   * chatbotApiService.createChatbotApi(chatbot);
   * ```
   *
   * @param chatbot - The data for the new chatbot    * @returns Observable<ChatbotDto> - The created chatbot details
   */
  createChatbotApi(chatbot: CreateChatbotDto): Observable<ChatbotDto> {
    const url = this.urlService.getChatbotUrl();

    return this.http.post<ChatbotDto>(url, chatbot).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Update a chatbot by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.chatbotApiService.updateChatbotById(chatbotId);
   *
   * // Use this in Interceptors, Guards and Functions
   * chatbotApiService.updateChatbotById(chatbotId);
   * ```
   *
   * @param chatbotId - The unique identifier of the chatbot
   * @returns Observable<ChatbotDto> - The chatbot details
   */
  updateChatbotById(chatbotId: string, updateChatbot: UpdateChatbotDto): Observable<ChatbotDto> {
    const url = this.urlService.getChatbotByIdUrl(chatbotId);

    return this.http.put<ChatbotDto>(url, updateChatbot).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a chatbot by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.chatbotApiService.deleteChatbot(chatbotId);
   *
   * // Use this in Interceptors, Guards and Functions
   * chatbotApiService.deleteChatbot(chatbotId);
   * ```
   *
   * @param chatbotId - The unique identifier of the chatbot
   * @returns Observable<ChatbotDto> - The chatbot details
   */
  deleteChatbot(chatbotId: string): Observable<void> {
    const url =  this.urlService.getChatbotByIdUrl(chatbotId);

    return this.http.delete<void>(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }
}
